/**
 * Adviser Dashboard — Marinus's primary view.
 *
 * Shows a pipeline overview of all files across all workflow stages,
 * statistics panel, and quick actions. Marinus can also draw and
 * process files from here.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus, CheckCircle2, Filter, Notebook, Paperclip,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { normalizePipeline } from '../services/pipelineService';
import QuoteCard from '../components/dashboard/QuoteCard';
import StatsBar from '../components/dashboard/StatsBar';
import SharedPipeline from '../components/dashboard/SharedPipeline';
import { getAllFiles } from '../api/filesApi';
import { getAllPrepRequests } from '../api/prepRequestsApi';
import { getActiveReviewClients, getCompletedReviewClients, getReviewGroups } from '../api/reviewClientsApi';
import { getAllReferrals } from '../api/referralsApi';
import CalendarCardModal from '../components/reviews/CalendarCardModal';
import ReferralCaptureModal from '../components/referrals/ReferralCaptureModal';
import { getNoteCountsByFile, getAllSystemNotes } from '../api/fileNotesApi';
import { getNoteCountsByPrep } from '../api/prepNotesApi';
import { getFlaggedCountsByFile } from '../api/caseScreenshotsApi';
import { getRandomQuote } from '../data/motivationalQuotes';
import { getGreeting, getCurrentMonthYear } from '../utils/dateHelpers';
import SLAWarningToast from '../components/notifications/SLAWarningToast';
import DashboardReturnToast, { useDashboardReturn } from '../components/shared/DashboardReturnToast';
import useAutoRefresh from '../hooks/useAutoRefresh';

const EMAIL_TO_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
};

function getFirstName(email) {
  return EMAIL_TO_NAME[(email || '').toLowerCase()] || 'there';
}

export default function AdviserDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [files, setFiles] = useState([]);
  // Preparation requests are a SEPARATE record type — loaded for pipeline
  // DISPLAY and the separated prep panel only. They never feed file stats.
  const [prepRequests, setPrepRequests] = useState([]);
  // Referral cards — a fourth separate record type, loaded for pipeline
  // DISPLAY only. They never feed file stats or the bonus counter.
  const [referrals, setReferrals] = useState([]);
  // "New Referral" capture modal (Adviser only — this view).
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [noteCounts, setNoteCounts] = useState({});
  // Per-file count of notes still awaiting acknowledgement — drives card
  // illumination (CLAUDE.md §31).
  // Role-keyed pending-acknowledgement counts (fileId → {role: n}). Scoped to
  // the viewer's active role inside SharedPipeline (owner-only illumination,
  // §31.4).
  const [pendingNoteCountsByRole, setPendingNoteCountsByRole] = useState({});
  // Prep-request note counts (total + role-keyed pending) — a separate
  // store from file notes; drives prep-card badges + illumination (§31).
  const [prepNoteCounts, setPrepNoteCounts] = useState({});
  const [prepPendingNoteCountsByRole, setPrepPendingNoteCountsByRole] = useState({});
  // System-event notes grouped by file id. Drives the bonus / SLA clock
  // and all six stat cards (see CLAUDE.md §21).
  const [systemNotesByFile, setSystemNotesByFile] = useState({});
  // Map of files.id → unresolved Layer 3 verification flags. Used to
  // surface a small amber dot on signed-stage kanban cards.
  const [flaggedCounts, setFlaggedCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterMonth, setFilterMonth] = useState(getCurrentMonthYear());
  const [deleteToast, setDeleteToast] = useState(null);
  const [deleteToastFading, setDeleteToastFading] = useState(false);
  const [quote] = useState(() => getRandomQuote());
  // Active (non-completed) review clients (CLAUDE.md §34.9): calendar
  // cards + the statistics bar. DISPLAY ONLY — never fed to
  // computeMonthStats / computeOverdue, the bonus counter, SLA, or the
  // concurrency invariants.
  const [reviewClients, setReviewClients] = useState([]);
  // Completed review clients — ONLY for the statistics bar's Annual
  // reviews "Completed This Year" tile (§37); never on the board.
  const [completedReviewClients, setCompletedReviewClients] = useState([]);
  // review_groups labels keyed by group id (§34.10) — one card per group.
  const [reviewGroupLabels, setReviewGroupLabels] = useState({});
  // The open calendar card UNIT descriptor (single client or group).
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewToast, setReviewToast] = useState(null);

  // `silent: true` re-runs the exact same queries WITHOUT toggling the
  // loading skeleton — used by the background auto-refresh so data updates
  // in place with no visual interruption.
  const loadFiles = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [data, noteData, flags, sysNotes, preps, prepNoteData, reviews, reviewGroups, referralRows, completedReviews] = await Promise.all([
        getAllFiles(),
        getNoteCountsByFile().catch(() => ({})),
        getFlaggedCountsByFile().catch(() => ({})),
        getAllSystemNotes().catch(() => ({})),
        getAllPrepRequests().catch(() => []),
        getNoteCountsByPrep().catch(() => ({})),
        getActiveReviewClients().catch(() => []),
        getReviewGroups().catch(() => ({})),
        // Referral cards — DISPLAY ONLY on this board: visible to every
        // role, openable by the Adviser alone (a later step wires the
        // detail view). Never fed to stats, the bonus counter, SLA or
        // concurrency. A missing table degrades to no referral cards.
        getAllReferrals().catch(() => []),
        // Statistics bar only (§37): a failure degrades to 0 completed.
        getCompletedReviewClients().catch(() => []),
      ]);
      const { counts = {}, pendingByRole = {} } = noteData || {};
      const { counts: prepCounts = {}, pendingByRole: prepPendingByRole = {} } = prepNoteData || {};
      setFiles(data);
      setNoteCounts(counts);
      setPendingNoteCountsByRole(pendingByRole);
      setPrepNoteCounts(prepCounts);
      setPrepPendingNoteCountsByRole(prepPendingByRole);
      setFlaggedCounts(flags);
      setSystemNotesByFile(sysNotes);
      setPrepRequests(preps);
      setReviewClients(reviews);
      setCompletedReviewClients(completedReviews);
      setReviewGroupLabels(reviewGroups);
      setReferrals(referralRows);
    } catch (err) {
      console.error('Failed to load files:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Return-to-dashboard (CLAUDE.md §39): toast + card highlight carried in
  // location.state by src/utils/returnToDashboard.js. The highlight waits
  // for the board to load; the state is cleared at once so a refresh never
  // replays it.
  const { toast: returnToast } = useDashboardReturn({ ready: !loading });

  // Silent auto-refresh: refetch when the tab regains visibility, plus
  // background polling every 45s while visible. Reuses loadFiles in
  // silent mode — state updates in place, no skeleton, no scroll reset.
  useAutoRefresh(() => loadFiles({ silent: true }));

  // Refresh note counts after the login catch-up gate (§31.9) acknowledges
  // notes, so cards stop flashing without a manual reload.
  useEffect(() => {
    const onAck = () => loadFiles();
    window.addEventListener('inbox:notes-acknowledged', onAck);
    return () => window.removeEventListener('inbox:notes-acknowledged', onAck);
  }, [loadFiles]);

  // First-load normalize: bring the pipeline into compliance with the
  // strict In Progress invariants (CLAUDE.md §7.6) — runs once per
  // dashboard mount, then refetches if anything changed.
  const normalizedRef = useRef(false);
  useEffect(() => {
    if (normalizedRef.current) return;
    if (loading) return;
    if (!files || files.length === 0) return;
    normalizedRef.current = true;
    (async () => {
      try {
        const applied = await normalizePipeline(files);
        if (applied > 0) {
          console.warn(`[pipeline-normalize] applied ${applied} op(s) — refetching files`);
          await loadFiles();
        }
      } catch (e) {
        console.warn('[pipeline-normalize] failed:', e);
      }
    })();
  }, [files, loading, loadFiles]);

  // Show delete success toast if redirected from file deletion
  useEffect(() => {
    if (location.state?.deleteSuccess) {
      setDeleteToast(location.state.deletedName || 'File');
      setDeleteToastFading(false);
      window.history.replaceState({}, document.title);
      const fadeTimer = setTimeout(() => setDeleteToastFading(true), 3000);
      const removeTimer = setTimeout(() => setDeleteToast(null), 3500);
      return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
    }
  }, [location.state]);

  // Review toast helper (§34.9). Local to the calendar-card feature;
  // touches no file/prep state or stats. (The dashboard's compact review
  // nudge and its add-to-pipeline handler were removed on 27 October 2026;
  // pulling reviews into the pipeline now happens on the Reviews page only.)
  const showReviewToast = (message) => {
    setReviewToast(message);
    setTimeout(() => setReviewToast(null), 3200);
  };

  // Modal reports confirmed writes (arrays — group transitions touch
  // every member); a completed unit stops matching the pipeline filter
  // and its card disappears.
  const onReviewUpdated = (updatedRows) => {
    const byId = {};
    updatedRows.forEach((r) => { byId[r.id] = r; });
    setReviewClients((prev) => prev.map((c) => byId[c.id] || c));
  };

  return (
    <div>
      {/* Return-to-dashboard toast (§39) */}
      <DashboardReturnToast message={returnToast} />

      {/* Delete success toast */}
      {deleteToast && (
        <motion.div
          style={styles.deleteToast}
          initial={{ opacity: 0, y: -20 }}
          animate={deleteToastFading ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
          transition={{ duration: deleteToastFading ? 0.5 : 0.3 }}
        >
          <CheckCircle2 size={16} color="#FFFFFF" />
          <span>{deleteToast} — deleted successfully</span>
        </motion.div>
      )}

      {/* Review toast (§34.9) — same visual as the delete toast. */}
      {reviewToast && (
        <motion.div
          style={styles.deleteToast}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CheckCircle2 size={16} color="#FFFFFF" />
          <span>{reviewToast}</span>
        </motion.div>
      )}

      {/* Greeting */}
      <div style={styles.greeting}>
        <h2 style={styles.greetingText}>{getGreeting(getFirstName(user?.email))}</h2>
        <div style={styles.greetingActions}>
          <button className="btn btn-primary" onClick={() => navigate('/submit')}>
            <Plus size={16} /> Submit New File
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/submit-prep')}>
            <Notebook size={16} /> Submit New Preparation Request
          </button>
          <button className="btn btn-outline" onClick={() => setShowReferralModal(true)}>
            <Paperclip size={16} /> New Referral
          </button>
        </div>
      </div>

      {/* Daily Motivational Quote */}
      <div style={styles.quoteWrap}>
        <QuoteCard quote={quote} />
      </div>

      {/* Month Filter — drives both the stat cards and the File Completed column */}
      <div style={styles.filterRow}>
        <Filter size={16} color="#6B7A8D" />
        <input
          type="month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          style={styles.monthInput}
        />
      </div>

      {/* Unified statistics bar (CLAUDE.md §37) — one set at a time via the
          Adviser's four-tab toggle (File folders / Prep requests / Referrals /
          Annual reviews). Each set is computed from its own source only
          (§21.1); the bar never combines figures across sets. */}
      <StatsBar
        files={files}
        systemNotesByFile={systemNotesByFile}
        prepRequests={prepRequests}
        referrals={referrals}
        reviewClients={reviewClients}
        completedReviewClients={completedReviewClients}
        filterMonth={filterMonth}
      />

      {/* Pipeline Kanban — file folders + prep-request notebook cards */}
      {loading ? (
        <div style={styles.loading}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton" style={{ height: '200px', borderRadius: '12px' }} />
          ))}
        </div>
      ) : (
        <SharedPipeline
          files={files}
          prepRequests={prepRequests}
          reviewClients={reviewClients}
          reviewGroupLabels={reviewGroupLabels}
          referrals={referrals}
          noteCounts={noteCounts}
          pendingNoteCountsByRole={pendingNoteCountsByRole}
          prepNoteCounts={prepNoteCounts}
          prepPendingNoteCountsByRole={prepPendingNoteCountsByRole}
          flaggedCounts={flaggedCounts}
          filterMonth={filterMonth}
          onFileClick={(f) => navigate(`/file/${f.id}`)}
          onPrepClick={(p) => navigate(`/prep/${p.id}`)}
          onReviewClick={(rcard) => setSelectedReview(rcard)}
          onReferralClick={(r) => navigate(`/referral/${r.id}`)}
        />
      )}

      {/* New-referral capture modal — Adviser only. On success the board
          refreshes silently so the new card appears in Inbox; the toast and
          card highlight come from the modal's returnToDashboard call (§39). */}
      <ReferralCaptureModal
        open={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        onCreated={() => {
          loadFiles({ silent: true });
        }}
      />

      {/* Calendar card detail modal (§34.9) — Adviser only. */}
      {selectedReview && (
        <CalendarCardModal
          card={selectedReview}
          onClose={() => setSelectedReview(null)}
          onUpdated={onReviewUpdated}
          onToast={showReviewToast}
        />
      )}

      {/* SLA Warning Toasts */}
      <SLAWarningToast files={files} />
    </div>
  );
}

const styles = {
  deleteToast: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.25rem',
    background: 'var(--cp-success)',
    color: '#FFFFFF',
    borderRadius: '10px',
    fontSize: '0.85rem',
    fontWeight: 600,
    zIndex: 2000,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    cursor: 'default',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  greeting: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  greetingActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    flexWrap: 'wrap',
  },
  greetingText: {
    fontSize: '1.4rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
  },
  quoteWrap: {
    maxWidth: '500px',
    marginBottom: '1.25rem',
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  monthInput: {
    padding: '0.35rem 0.6rem',
    border: '1px solid #D0DAE6',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    width: 'auto',
  },
  loading: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
  },
  pipeline: {
    display: 'flex',
    gap: '0.75rem',
    overflowX: 'auto',
    paddingBottom: '1rem',
    alignItems: 'flex-start',
  },
  column: {
    minWidth: '180px',
    flex: '1 1 180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    verticalAlign: 'top',
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    marginBottom: '0.75rem',
    padding: '0.5rem 0',
    height: '48px',
    minHeight: '48px',
  },
  columnLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: 'var(--cp-text)',
    flex: 1,
  },
  columnCount: {
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '0.1rem 0.4rem',
    borderRadius: '999px',
  },
  columnCards: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    gap: '0.5rem',
  },
  emptyColumn: {
    fontSize: '0.8rem',
    color: '#6B7A8D',
    textAlign: 'center',
    padding: '2rem 0',
    fontStyle: 'italic',
  },
};
